import React, { useState, useEffect } from "react";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: null, text: "" });
  const [isMobile, setIsMobile] = useState(false);
  const [apiBase, setApiBase] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const probe = async (base) => {
      try {
        const r = await fetch(`${base}/health`, { method: "GET" });
        return r.ok;
      } catch (_) {
        return false;
      }
    };
    (async () => {
      if (API_BASE) {
        const ok = await probe(API_BASE);
        if (ok) { setApiBase(API_BASE); return; }
      }
      const okLocal = await probe("http://localhost:5001");
      if (okLocal) setApiBase("http://localhost:5001");
    })();
  }, []);

  const handleSubmit = async () => {
    if (!email) {
      setStatus({ type: "error", text: "Please enter your email address to subscribe." });
      return;
    }
    if (!email.includes("@")) {
      setStatus({ type: "error", text: "Please enter a valid email address." });
      return;
    }
    if (!apiBase) {
      setStatus({ type: "success", text: `Thank you for subscribing, ${email}!` });
      setTimeout(() => setStatus({ type: null, text: "" }), 3000);
      setEmail("");
      return;
    }
    try {
      const res = await fetch(`${apiBase}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStatus({ type: "success", text: `Thank you for subscribing, ${email}!` });
        setTimeout(() => setStatus({ type: null, text: "" }), 3000);
        setEmail("");
        return;
      }
      setStatus({ type: "error", text: "Subscription failed. Please try again later." });
    } catch (_) {
      setStatus({ type: "error", text: "Subscription failed. Please try again later." });
    }
  };

  const messageColor = status.type === "error" ? "#e74c3c" : "#009999";

  return (
    <div
      style={{
        position: "relative",
        backgroundColor: "#FFFFFF",
        minHeight: "380px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        fontFamily: "'Poppins', sans-serif",
        padding: isMobile ? "40px 20px" : 0,
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* Colored Dots - Hidden on mobile for cleaner look */}
      {!isMobile && (
        <>
          <div
            style={{
              position: "absolute",
              top: "25%",
              left: "8%",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#e74c3c",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "18%",
              left: "18%",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#2ecc71",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "55%",
              left: "5%",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#f1c40f",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "70%",
              left: "10%",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#9b59b6",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "40%",
              left: "3%",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#3498db",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "85%",
              left: "15%",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#e67e22",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "15%",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#e67e22",
            }}
          />
        </>
      )}

      {/* Concentric Circles - Scaled down on mobile */}
      <div
        style={{
          position: "absolute",
          right: isMobile ? "-150px" : "-100px",
          top: isMobile ? "10%" : "20%",
          transform: "translateY(-50%)",
          width: isMobile ? "200px" : "400px",
          height: isMobile ? "150px" : "300px",
          opacity: isMobile ? 0.5 : 1,
        }}
      >
        <div
          style={{
            position: "absolute",
            width: isMobile ? "175px" : "350px",
            height: isMobile ? "175px" : "350px",
            borderRadius: "50%",
            border: `${isMobile ? "18px" : "35px"} solid #009999`,
            boxSizing: "border-box",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: isMobile ? "25px" : "50px",
            left: isMobile ? "25px" : "50px",
            width: isMobile ? "125px" : "250px",
            height: isMobile ? "125px" : "250px",
            borderRadius: "50%",
            border: `${isMobile ? "15px" : "30px"} solid #009999`,
            boxSizing: "border-box",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: isMobile ? "48px" : "95px",
            left: isMobile ? "50px" : "100px",
            width: isMobile ? "75px" : "150px",
            height: isMobile ? "75px" : "150px",
            borderRadius: "50%",
            backgroundColor: "#009999",
          }}
        />
      </div>

      {/* Main Content */}
      <div
        style={{
          textAlign: "center",
          zIndex: 10,
          maxWidth: "500px",
          width: "100%",
          padding: isMobile ? "0 10px" : 0,
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? "22px" : "28px",
            fontWeight: "600",
            color: "#2d3436",
            marginBottom: "12px",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Never Miss a Moment
        </h1>
        <p
          style={{
            fontSize: isMobile ? "12px" : "13px",
            color: "#636e72",
            marginBottom: isMobile ? "24px" : "28px",
            lineHeight: "1.6",
            fontFamily: "'Poppins', sans-serif",
            fontWeight: "400",
            padding: isMobile ? "0 10px" : 0,
          }}
        >
          Subscribe and Get the latest news, promotions, and event updates
          {!isMobile && <br />} straight to your inbox.
        </p>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "center",
            alignItems: "center",
            gap: isMobile ? "12px" : "0",
          }}
        >
          <input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status.text) setStatus({ type: null, text: "" });
            }}
            style={{
              padding: "14px 20px",
              fontSize: "13px",
              border: "1px solid #ccc",
              outline: "none",
              width: isMobile ? "100%" : "280px",
              maxWidth: isMobile ? "300px" : "none",
              borderRadius: isMobile ? "4px" : "4px 0 0 4px",
              fontFamily: "'Poppins', sans-serif",
              color: "#636e72",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              boxSizing: "border-box"
              
            }}
          />
          <button
            onClick={handleSubmit}
            style={{
              padding: "14px 24px",
              fontSize: "12px",
              fontWeight: "600",
              color: "#ffffff",
              backgroundColor: "#009999",
              border: "none",
              borderRadius: isMobile ? "4px" : "0 4px 4px 0",
              cursor: "pointer",
              fontFamily: "'Poppins', sans-serif",
              letterSpacing: "0.5px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              transition: "background-color 0.2s",
              width: isMobile ? "100%" : "auto",
              maxWidth: isMobile ? "300px" : "none",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#007777")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#009999")
            }
          >
            SUBSCRIBE
          </button>
        </div>

        {status.text && (
          <p
            style={{
              marginTop: "16px",
              color: messageColor,
              fontSize: isMobile ? "13px" : "14px",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: status.type === "error" ? "600" : "500",
              padding: isMobile ? "0 10px" : 0,
            }}
          >
            {status.text}
          </p>
        )}
      </div>
    </div>
  );
};

export default Newsletter;
